// Real choices for the conditions on a policy exception.
//
// Why this exists: several predicates are matched by ID, not by name.
// `department_in` / `designation_in` compare against `Employee.department_id`
// and `designation_id`, and `employee_in` against `Employee.id` — so a free
// text box invited people to type "Sales" into a rule that can only ever match
// a UUID. The rule then saved happily and silently matched nobody.
//
// Every value here is therefore what the backend resolver actually compares
// (see hrms/utils/policy_resolver.py `_predicate`), while the label is what a
// human recognises.

import { listRoles } from '../../api/roles';
import departmentApi from '../../api/departmentApi';
import designationApi from '../../api/designationApi';
import { getEmployees } from '../../api/employees';

export interface PredicateOption {
  value: string;
  label: string;
}

// Vocabularies the engine defines itself, so there is nothing to fetch.
// Matching is normalized on the server (lowercase, spaces/underscores → "-"),
// which is why "in-office" and "in_office" both land on the same mode.
const STATIC_OPTIONS: Record<string, PredicateOption[]> = {
  gender: [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'others', label: 'Others' },
  ],
  work_mode_in: [
    { value: 'in-office', label: 'In office' },
    { value: 'remote', label: 'Remote' },
    { value: 'hybrid', label: 'Hybrid' },
  ],
  work_location_in: [
    { value: 'in_office', label: 'In office' },
    { value: 'work_from_home', label: 'Work from home' },
    { value: 'client_site', label: 'Client site' },
    { value: 'department_visit', label: 'Department visit' },
  ],
};

/** These list endpoints disagree about their envelope, so unwrap defensively. */
const asArray = (payload: unknown): any[] => {
  if (Array.isArray(payload)) return payload;
  const bag = payload as Record<string, unknown> | null;
  for (const key of ['departments', 'designations', 'employees', 'data', 'results', 'items']) {
    if (Array.isArray(bag?.[key])) return bag![key] as any[];
  }
  return [];
};

const named = (row: any): string =>
  String(row?.name ?? row?.title ?? row?.label ?? row?.id ?? '').trim();

async function loadRoles(): Promise<PredicateOption[]> {
  const roles = await listRoles();
  // The server normalizes both sides before comparing, so role_key and name
  // resolve identically — role_key is the stabler of the two.
  return roles.map((r) => ({ value: r.role_key || r.name, label: r.name }));
}

async function loadDepartments(): Promise<PredicateOption[]> {
  const rows = asArray(await departmentApi.getDepartments());
  return rows
    .filter((d) => d?.id != null)
    .map((d) => ({ value: String(d.id), label: named(d) || String(d.id) }));
}

async function loadDesignations(): Promise<PredicateOption[]> {
  const rows = asArray(await designationApi.getDesignations());
  return rows
    .filter((d) => d?.id != null)
    .map((d) => ({ value: String(d.id), label: named(d) || String(d.id) }));
}

// /employeesAll caps `limit` at 100 and reports the real `total`, so a company
// with more than 100 people would otherwise get a picker that silently omits
// most of them. Page until the total is covered.
const PAGE = 100;
const MAX_EMPLOYEES = 2000;

async function loadEmployees(): Promise<PredicateOption[]> {
  const out: PredicateOption[] = [];
  for (let offset = 0; offset < MAX_EMPLOYEES; offset += PAGE) {
    const res = await getEmployees({ limit: PAGE, offset });
    const rows = asArray(res);
    rows.forEach((e) => {
      if (e?.id == null) return;
      const name = [e.first_name, e.last_name].filter(Boolean).join(' ').trim();
      const label = name || e.employeeID || e.email || String(e.id);
      out.push({
        value: String(e.id),
        label: e.employeeID && name ? `${name} (${e.employeeID})` : label,
      });
    });
    const total = Number((res as any)?.total ?? out.length);
    if (rows.length < PAGE || out.length >= total) break;
  }
  return out;
}

const LOADERS: Record<string, () => Promise<PredicateOption[]>> = {
  role_in: loadRoles,
  role_not_in: loadRoles,
  department_in: loadDepartments,
  designation_in: loadDesignations,
  employee_in: loadEmployees,
  employee_not_in: loadEmployees,
};

/** True when this condition can be picked from a list instead of typed. */
export function hasOptions(key: string): boolean {
  return key in STATIC_OPTIONS || key in LOADERS;
}

// Fetched lists are cached for the session: the dialog is opened and closed
// repeatedly while building up exceptions, and the department list does not
// change between two of those.
const cache = new Map<string, PredicateOption[]>();

export function primeOptionCache(key: string, options: PredicateOption[]): void {
  cache.set(key, options);
}

export function clearOptionCache(): void {
  cache.clear();
}

/** The choices for a condition, or null when it is free-form (a number, say). */
export async function loadPredicateOptions(
  key: string,
): Promise<PredicateOption[] | null> {
  if (STATIC_OPTIONS[key]) return STATIC_OPTIONS[key];
  const cached = cache.get(key);
  if (cached) return cached;

  const loader = LOADERS[key];
  if (!loader) return null;

  const options = await loader();
  cache.set(key, options);
  return options;
}
